/**
 * CreateZoneLockOverlay — 3D label that sits above the local player's create
 * zone when the visible-sidebet cap is reached. Lock icon + "PLEASE WAIT"
 * copy. Tells the player their bet is gated until a sidebet resolves; pairs
 * with BetZoneManager.setCreateZoneLocked() to also skip the zone in drop
 * raycasts so a stray bill drop doesn't get sent + rejected.
 */

import * as THREE from 'three';
import { TSS } from '../../tss/TSS';
import { UINodeConfig } from '../../tss/types';

const LOCK_ICON_PATH = '/assets/icons/ui/lock-icon.png';

export class CreateZoneLockOverlay {
  private static lockIcon: HTMLImageElement | null = null;
  private static lockReady = false;
  private static listeners = new Set<() => void>();

  private component: TSS;
  private redrawListener = () => {
    this.component.setTree(this.buildTree());
    this.component.render();
  };

  constructor(parent: THREE.Object3D) {
    CreateZoneLockOverlay.loadIcon();

    this.component = new TSS(this.buildTree(), {
      // Square mesh, lays flat on the table (rotated below) so it integrates
      // with the scene depth — no billboard occlusion of the wallet /
      // accumulator UI behind/in front of it.
      worldWidth: 1.6,
      worldHeight: 1.6,
      mode: 'mesh',
      resolution: 1,
      // depthTest enabled so the wallet (closer to camera in world space)
      // correctly occludes the icon. depthWrite stays false so transparent
      // pixels of the lock PNG don't punch holes in the depth buffer.
      depthTest: true,
      depthWrite: false,
      renderOrder: 100,
    });

    // Rotate the plane to lie flat on the table — same -90° X rotation
    // BettingZone uses for its ground-aligned ring.
    this.component.object3D.rotation.x = -Math.PI / 2;

    parent.add(this.component.object3D);
    this.component.setVisible(false);
    // Sit centered in the zone, slightly above the floor to avoid z-fighting.
    this.component.setPosition(0, 0.02, 0);

    if (!CreateZoneLockOverlay.lockReady) {
      CreateZoneLockOverlay.listeners.add(this.redrawListener);
    }
  }

  private static loadIcon(): void {
    if (CreateZoneLockOverlay.lockIcon) return;
    const img = new Image();
    img.onload = () => {
      CreateZoneLockOverlay.lockReady = true;
      for (const listener of CreateZoneLockOverlay.listeners) listener();
      CreateZoneLockOverlay.listeners.clear();
    };
    img.src = LOCK_ICON_PATH;
    CreateZoneLockOverlay.lockIcon = img;
  }

  private buildTree(): UINodeConfig {
    const icon = CreateZoneLockOverlay.lockReady ? CreateZoneLockOverlay.lockIcon ?? undefined : undefined;
    return {
      tag: 'root',
      style: { direction: 'column', alignItems: 'center' },
      children: [
        {
          tag: 'icon',
          image: icon,
          style: { width: 140, height: 140 },
        },
      ],
    };
  }

  setPosition(x: number, y: number, z: number): void {
    this.component.setPosition(x, y, z);
  }

  setVisible(visible: boolean): void {
    this.component.setVisible(visible);
  }

  attachTo(parent: THREE.Object3D): void {
    this.component.attachTo(parent);
  }

  dispose(): void {
    CreateZoneLockOverlay.listeners.delete(this.redrawListener);
    this.component.dispose();
  }
}
